import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
// (placeholder) kept minimal imports; remove unused selects for now
import { Alert, AlertDescription } from '@/components/ui/alert'
import apiClient from '@/lib/api/client'
import { tenantSubmitCertification as sdkSubmitCertification } from '@/lib/api/generated/sdk.gen'

interface OnboardingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  status?: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'NONE'
  rejectionReasons?: Array<{ id?: number; reasonCategory?: string; reasonDetail?: string; requiredActions?: string; reviewerName?: string; reviewTime?: string }>
}

export default function OnboardingDialog({ open, onOpenChange, status = 'NONE', rejectionReasons = [] }: OnboardingDialogProps) {
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState({
    // 认证方式
    verificationMethod: 'LEGAL_PERSON',
    // 企业基本信息（精简）
    companyName: '',
    legalPerson: '',
    // 资质信息
    businessLicense: '',
    licenseUrl: '',
    // 法人信息（作为联系人）
    contactPerson: '',
    contactPhone: '',
    contactIdNumber: '',
    contactEmail: '',
    // 法人身份证影像
    idCardFrontUrl: '',
    idCardBackUrl: '',
  })
  const [licensePreview, setLicensePreview] = useState<string>('')
  const [idFrontPreview, setIdFrontPreview] = useState<string>('')
  const [idBackPreview, setIdBackPreview] = useState<string>('')

  const latestRejection = useMemo(() => (rejectionReasons && rejectionReasons.length > 0 ? rejectionReasons[0] : undefined), [rejectionReasons])

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const validate = () => {
    const next: Record<string, string> = {}
    if (!formData.companyName.trim()) next.companyName = '请输入企业名称'
    if (!formData.businessLicense.trim()) next.businessLicense = '请输入营业执照号'
    if (!formData.licenseUrl.trim()) next.licenseUrl = '请上传营业执照图片'
    if (!formData.idCardFrontUrl.trim()) next.idCardFrontUrl = '请上传法人身份证正面'
    if (!formData.idCardBackUrl.trim()) next.idCardBackUrl = '请上传法人身份证反面'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const onSubmit = async () => {
    if (!validate()) return
    setSubmitting(true)
    try {
      const payload: any = {
        verificationMethod: formData.verificationMethod || 'LEGAL_PERSON',
        companyName: formData.companyName,
        businessLicense: formData.businessLicense,
        legalPerson: formData.legalPerson,
        registeredCapital: null,
        businessScope: null,
        registeredAddress: null,
        businessAddress: null,
        licenseUrl: formData.licenseUrl,
        idCardFrontUrl: formData.idCardFrontUrl,
        idCardBackUrl: formData.idCardBackUrl,
        licenseExpireDate: null,
        contactName: formData.contactPerson,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        contactIdNumber: formData.contactIdNumber,
      }
      await sdkSubmitCertification({ client: apiClient, body: payload, responseStyle: 'fields', throwOnError: true })
      // 刷新企业认证状态与拒绝原因
      queryClient.invalidateQueries({ queryKey: ['tenant', 'certification'] })
      queryClient.invalidateQueries({ queryKey: ['tenant', 'certification', 'rejectionReasons'] })
      onOpenChange(false)
    } catch (e: any) {
      // 粗略错误提示映射到顶部警告
      setErrors(prev => ({ ...prev, _submit: e?.message || '提交失败，请稍后重试' }))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>企业认证</DialogTitle>
          {status === 'REJECTED' && latestRejection && (
            <Alert className="mt-3" variant="destructive">
              <AlertDescription>
                <div className="space-y-1">
                  <div>审核未通过：{latestRejection.reasonCategory || 'OTHER'}</div>
                  {latestRejection.reasonDetail && <div className="text-sm opacity-90">原因：{latestRejection.reasonDetail}</div>}
                  {latestRejection.requiredActions && <div className="text-sm opacity-90">建议：{latestRejection.requiredActions}</div>}
                </div>
              </AlertDescription>
            </Alert>
          )}
          {errors._submit && (
            <Alert className="mt-3">
              <AlertDescription>{errors._submit}</AlertDescription>
            </Alert>
          )}
        </DialogHeader>

        <div className="mt-2 space-y-10">
          {/* 企业基本（精简必填） */}
          <section>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">企业基本</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-sm">企业名称 *</Label>
                <Input id="companyName" placeholder="请输入企业全称" value={formData.companyName} onChange={e => updateField('companyName', e.target.value)} className={errors.companyName ? 'border-red-500' : ''} />
                {errors.companyName && <p className="text-xs text-red-500">{errors.companyName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="legalPerson" className="text-sm">法定代表人</Label>
                <Input id="legalPerson" placeholder="如营业执照所示" value={formData.legalPerson} onChange={e => updateField('legalPerson', e.target.value)} />
              </div>
            </div>
          </section>

          {/* 企业资质（精简必填） */}
          <section>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">企业资质</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div className="space-y-2 md:col-span-2">
                <Label>认证方式</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Button type="button" variant={formData.verificationMethod==='LEGAL_PERSON'?'default':'outline'} onClick={()=>updateField('verificationMethod','LEGAL_PERSON')}>法人认证（当前支持）</Button>
                  <Button type="button" variant="outline" disabled>企业邮箱认证（预留）</Button>
                  <Button type="button" variant="outline" disabled>授权书认证（预留）</Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessLicense" className="text-sm">统一社会信用代码 *</Label>
                <Input id="businessLicense" placeholder="请输入18位统一社会信用代码" value={formData.businessLicense} onChange={e => updateField('businessLicense', e.target.value)} className={errors.businessLicense ? 'border-red-500' : ''} />
                {errors.businessLicense && <p className="text-xs text-red-500">{errors.businessLicense}</p>}
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm">营业执照上传 *</Label>
                <div className={`border-2 border-dashed rounded-md p-4 ${errors.licenseUrl ? 'border-red-500' : 'border-gray-300'}`}>
                  {licensePreview ? (
                    <div className="flex items-center justify-between">
                      <img src={licensePreview} alt="license" className="h-16 rounded" />
                      <Button type="button" variant="outline" size="sm" onClick={() => { setLicensePreview(''); updateField('licenseUrl','') }}>重新选择</Button>
                    </div>
                  ) : (
                    <label className="text-sm text-gray-600 cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const url = URL.createObjectURL(file);
                          setLicensePreview(url);
                          updateField('licenseUrl', url);
                        }
                      }} />
                      点击选择或拖拽上传营业执照图片
                    </label>
                  )}
                </div>
                {errors.licenseUrl && <p className="text-xs text-red-500">{errors.licenseUrl}</p>}
              </div>
            </div>
          </section>

          {/* 联系人信息（精简） */}
          <section>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">联系人信息</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div className="space-y-2">
                <Label htmlFor="contactPerson" className="text-sm">联系人姓名</Label>
                <Input id="contactPerson" placeholder="与证件一致" value={formData.contactPerson} onChange={e => updateField('contactPerson', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPhone" className="text-sm">联系人手机</Label>
                <Input id="contactPhone" placeholder="用于联系与通知" value={formData.contactPhone} onChange={e => updateField('contactPhone', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactIdNumber" className="text-sm">联系人身份证号</Label>
                <Input id="contactIdNumber" placeholder="18位或15位身份证号" value={formData.contactIdNumber} onChange={e => updateField('contactIdNumber', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactEmail" className="text-sm">联系人邮箱</Label>
                <Input id="contactEmail" type="email" placeholder="用于收取审核通知" value={formData.contactEmail} onChange={e => updateField('contactEmail', e.target.value)} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm">身份证正面 *</Label>
                <div className={`border-2 border-dashed rounded-md p-4 ${errors.idCardFrontUrl ? 'border-red-500' : 'border-gray-300'}`}>
                  {idFrontPreview ? (
                    <div className="flex items-center justify-between">
                      <img src={idFrontPreview} alt="id-front" className="h-16 rounded" />
                      <Button type="button" variant="outline" size="sm" onClick={() => { setIdFrontPreview(''); updateField('idCardFrontUrl','') }}>重新选择</Button>
                    </div>
                  ) : (
                    <label className="text-sm text-gray-600 cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) { const url = URL.createObjectURL(file); setIdFrontPreview(url); updateField('idCardFrontUrl', url); }
                      }} />
                      点击选择或拖拽上传身份证正面
                    </label>
                  )}
                </div>
                {errors.idCardFrontUrl && <p className="text-xs text-red-500">{errors.idCardFrontUrl}</p>}
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm">身份证反面 *</Label>
                <div className={`border-2 border-dashed rounded-md p-4 ${errors.idCardBackUrl ? 'border-red-500' : 'border-gray-300'}`}>
                  {idBackPreview ? (
                    <div className="flex items-center justify-between">
                      <img src={idBackPreview} alt="id-back" className="h-16 rounded" />
                      <Button type="button" variant="outline" size="sm" onClick={() => { setIdBackPreview(''); updateField('idCardBackUrl','') }}>重新选择</Button>
                    </div>
                  ) : (
                    <label className="text-sm text-gray-600 cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) { const url = URL.createObjectURL(file); setIdBackPreview(url); updateField('idCardBackUrl', url); }
                      }} />
                      点击选择或拖拽上传身份证反面
                    </label>
                  )}
                </div>
                {errors.idCardBackUrl && <p className="text-xs text-red-500">{errors.idCardBackUrl}</p>}
              </div>
            </div>
          </section>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>取消</Button>
            <Button onClick={onSubmit} disabled={submitting} className="min-w-[120px]">{submitting ? '提交中...' : '提交认证'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
